import { Controller } from "@hotwired/stimulus"
import { getJSON, listResults, sendJSON } from "../api.js"

// Reusable top-up component: "Dowolna kwota" card + package cards from the API, a purchase summary with an
// optional voucher code, and the hand-off to the payment gateway.
// Config sources, later wins: data-attributes -> JSON in the template (config-id) -> JSON from the API (config-url).
// Keys: packagesUrl, checkUrl, topupUrl, acceptTermsUrl, initialPackage, labels. Amounts are computed by the API.
// 23% VAT as integer arithmetic on grosze (123/100), half-up like the server's `to_gross` - never float.
const VAT_PERCENT = 123

// The first message string found in an error payload of any shape the API sends: a string, a list of strings, or the
// field map {"code": ["..."]} / {"voucher_code": ["..."]} / {"detail": "..."} (also wrapped in {"error": {"detail": ...}}).
function firstMessage(value) {
  if (typeof value === "string") return value
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = firstMessage(item)
      if (found) return found
    }
  } else if (value && typeof value === "object") {
    for (const item of Object.values(value)) {
      const found = firstMessage(item)
      if (found) return found
    }
  }
  return ""
}

// Gross PLN of a net PLN amount: net in integer grosze x 123, +50 then floor-divide by 100 = half-up.
function grossFromNet(net) {
  const netGrosze = Math.round(net * 100)
  return Math.floor((netGrosze * VAT_PERCENT + 50) / 100) / 100
}

// Same shape as the public pricing page (credits/services/public_package_cards.py:format_pln):
// thousands grouped with a no-break space, comma decimals, grosze only when present. (Intl pl-PL does not
// group 4-digit numbers, so it is done by hand.)
function formatPln(value) {
  const num = Number(value)
  if (!Number.isFinite(num)) return "—"
  const [whole, cents] = (Math.round(num * 100) / 100).toFixed(2).split(".")
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, "\u00a0")
  return `${cents === "00" ? grouped : `${grouped},${cents}`} zł`
}
const DEFAULT_LABELS = {
  loadFailed: "Nie udało się załadować pakietów. Odśwież stronę.",
  empty: "Brak dostępnych pakietów. Skorzystaj z opcji „Dowolna kwota”.",
  amountRequired: "Podaj kwotę doładowania.",
  codeRequired: "Wpisz kod.",
  voucherFailed: "Nie udało się sprawdzić kodu.",
  topupFailed: "Nie udało się rozpocząć płatności.",
  termsAcceptFailed: "Nie udało się zapisać akceptacji regulaminu.",
  rateLimited: "Za dużo prób, spróbuj za chwilę.",
  voucherOk: "Kod działa: dodatkowo",
}

export default class extends Controller {
  static targets = [
    "grid", "freeCard", "freeInput", "freeGross", "cardTemplate", "status",
    "summary", "payGross", "bonusRow", "bonus", "total",
    "voucherBlock", "voucherInput", "voucherResult", "payBtn", "error",
    "termsModal",
  ]
  static values = {
    packagesUrl: String,
    checkUrl: String,
    topupUrl: String,
    acceptTermsUrl: String,
    initialPackage: Number,
    configId: String,
    configUrl: String,
  }

  async connect() {
    this.packages = []
    this.selected = null // null | "free" | package object
    this.voucherNet = 0
    this.sending = false
    this.acceptingTerms = false
    this.cfg = await this.#buildConfig()
    if (!this.element.isConnected) return
    await this.#loadPackages()
  }

  // --- actions ---

  select(event) {
    const id = Number(event.currentTarget.dataset.packageId)
    const pack = this.packages.find((p) => p.id === id)
    if (pack) this.#choose(pack)
  }

  selectFree() {
    if (this.selected !== "free") this.#choose("free")
  }

  onFreeInput() {
    this.selectFree()
    this.#renderFreeGross()
    this.#renderSummary()
  }

  onCodeInput() {
    this.#resetVoucher()
    this.#renderSummary()
  }

  async checkVoucher() {
    if (!this.selected || this.selected === "free") return
    const code = this.voucherInputTarget.value.trim()
    if (!code) {
      this.#voucherMessage(this.#label("codeRequired"), true)
      return
    }
    const res = await sendJSON("POST", this.cfg.checkUrl, { code, package: this.selected.id })
    if (!res.ok) {
      this.voucherNet = 0
      this.#voucherMessage(this.#errorText(res, "voucherFailed"), true)
      this.#renderSummary()
      return
    }
    this.voucherNet = Number(res.data?.amount_net) || 0
    this.#voucherMessage(`${this.#label("voucherOk")} ${formatPln(res.data?.amount_net)}`, false)
    this.#renderSummary()
  }

  async pay() {
    if (this.sending || !this.selected) return
    const body = this.#topupBody()
    if (!body) {
      this.#error(this.#label("amountRequired"))
      return
    }
    this.sending = true
    this.payBtnTarget.disabled = true
    this.#error(null)
    const res = await sendJSON("POST", this.cfg.topupUrl, body)
    if (!res.ok) {
      this.sending = false
      this.#renderSummary()
      // Mirror checkout: accept the current terms in a modal, then retry the action.
      if (res.status === 403 && res.data?.code === "terms_required") {
        if (this.hasTermsModalTarget) this.termsModalTarget.hidden = false
        return
      }
      this.#error(this.#errorText(res, "topupFailed"))
      return
    }
    if (res.data?.redirect_url) {
      this.redirect(res.data.redirect_url)
      return
    }
    this.dispatch("paid", { detail: { topUp: res.data?.top_up } })
  }

  async acceptTerms() {
    if (this.acceptingTerms) return
    this.acceptingTerms = true
    const res = await sendJSON("POST", this.cfg.acceptTermsUrl, {})
    this.acceptingTerms = false
    if (!res.ok) {
      this.#error(this.#label("termsAcceptFailed"))
      return
    }
    this.closeTerms()
    await this.pay()
  }

  closeTerms() {
    if (this.hasTermsModalTarget) this.termsModalTarget.hidden = true
  }

  stop(event) {
    event.stopPropagation()
  }

  // Separated for spying in tests (jsdom has no navigation).
  redirect(url) {
    window.location.assign(url)
  }

  // --- private ---

  async #buildConfig() {
    const cfg = {
      packagesUrl: this.packagesUrlValue,
      checkUrl: this.checkUrlValue,
      topupUrl: this.topupUrlValue,
      acceptTermsUrl: this.acceptTermsUrlValue,
      initialPackage: this.hasInitialPackageValue ? this.initialPackageValue : 0,
      labels: { ...DEFAULT_LABELS },
    }
    const merge = (src) => {
      if (!src || typeof src !== "object") return
      for (const key of ["packagesUrl", "checkUrl", "topupUrl", "acceptTermsUrl", "initialPackage"]) {
        if (src[key]) cfg[key] = src[key]
      }
      if (src.labels) Object.assign(cfg.labels, src.labels)
    }
    if (this.configIdValue) {
      try {
        merge(JSON.parse(document.getElementById(this.configIdValue)?.textContent ?? "null"))
      } catch { /* a broken inline config falls back to the attributes */ }
    }
    if (this.configUrlValue) {
      try { merge(await getJSON(this.configUrlValue)) } catch { /* API config is optional */ }
    }
    return cfg
  }

  #label(key) {
    return this.cfg.labels[key] ?? DEFAULT_LABELS[key]
  }

  #errorText(res, fallbackKey) {
    if (res.status === 429) return this.#label("rateLimited")
    return firstMessage(res.data?.error?.detail ?? res.data?.detail) || this.#label(fallbackKey)
  }

  async #loadPackages() {
    if (!this.cfg.packagesUrl) { // read-only preview (tutorial editor): no data source, the free-amount card only
      this.#status(null)
      return
    }
    let list
    try {
      list = await getJSON(this.cfg.packagesUrl)
    } catch {
      this.#status(this.#label("loadFailed"))
      return
    }
    this.packages = listResults(list)
    this.#status(this.packages.length ? null : this.#label("empty"))
    for (const pack of this.packages) this.gridTarget.appendChild(this.#buildCard(pack))
    if (this.cfg.initialPackage) {
      const pre = this.packages.find((p) => p.id === Number(this.cfg.initialPackage))
      if (pre) this.#choose(pre)
    }
  }

  #buildCard(pack) {
    const card = this.cardTemplateTarget.content.firstElementChild.cloneNode(true)
    card.dataset.packageId = String(pack.id)
    const set = (field, text) => {
      const el = card.querySelector(`[data-field="${field}"]`)
      if (el) el.textContent = text
    }
    set("name", pack.name)
    set("net", formatPln(pack.amount_net))
    set("gross", formatPln(pack.amount_gross))
    set("bonus", formatPln(pack.bonus_net))
    set("total", formatPln(pack.total_net))
    set("description", pack.description || "")
    const badge = card.querySelector('[data-field="badge"]')
    if (badge) {
      badge.textContent = pack.badge || ""
      badge.hidden = !pack.badge
    }
    return card
  }

  #choose(target) {
    this.selected = target
    this.#resetVoucher()
    this.#error(null)
    const free = target === "free"
    this.freeCardTarget.toggleAttribute("data-selected", free)
    for (const card of this.gridTarget.querySelectorAll("[data-package-id]")) {
      const on = !free && Number(card.dataset.packageId) === target.id
      card.toggleAttribute("data-selected", on)
      card.querySelector("button")?.setAttribute("aria-pressed", String(on))
    }
    this.summaryTarget.hidden = false
    this.voucherBlockTarget.hidden = free
    this.#renderSummary()
  }

  #resetVoucher() {
    this.voucherNet = 0
    this.#voucherMessage(null)
  }

  #freeNet() {
    const value = Number(String(this.freeInputTarget.value).replace(",", "."))
    return Number.isFinite(value) && value > 0 ? value : 0
  }

  #renderFreeGross() {
    const net = this.#freeNet()
    this.freeGrossTarget.textContent = net ? formatPln(grossFromNet(net)) : "—"
  }

  #renderSummary() {
    if (!this.selected) return
    if (this.selected === "free") {
      const net = this.#freeNet()
      this.payGrossTarget.textContent = net ? formatPln(grossFromNet(net)) : "—"
      this.bonusRowTarget.hidden = true
      this.totalTarget.textContent = net ? formatPln(net) : "—"
      this.payBtnTarget.disabled = this.sending || !net
      return
    }
    const pack = this.selected
    this.payGrossTarget.textContent = formatPln(pack.amount_gross)
    const bonus = Number(pack.bonus_net) || 0
    this.bonusRowTarget.hidden = bonus <= 0
    this.bonusTarget.textContent = formatPln(pack.bonus_net)
    const total = (Number(pack.total_net) || 0) + this.voucherNet
    this.totalTarget.textContent = formatPln(Math.round(total * 100) / 100)
    this.payBtnTarget.disabled = this.sending
  }

  #topupBody() {
    if (this.selected === "free") {
      const net = this.#freeNet()
      return net ? { amount_net: net.toFixed(2) } : null
    }
    const body = { package: this.selected.id }
    const code = this.voucherInputTarget.value.trim()
    if (code) body.voucher_code = code
    return body
  }

  #voucherMessage(message, isError = false) {
    this.voucherResultTarget.hidden = message === null
    if (message !== null) {
      this.voucherResultTarget.textContent = message
      this.voucherResultTarget.dataset.tone = isError ? "error" : "ok"
    }
  }

  #error(message) {
    this.errorTarget.hidden = message === null
    if (message !== null) this.errorTarget.textContent = message
  }

  #status(message) {
    this.statusTarget.hidden = message === null
    if (message !== null) this.statusTarget.textContent = message
  }
}
